/**
 * Quando una lista puo' DICHIARARE di essere vuota.
 *
 * 🔴 IL GUASTO CHE QUESTO FILE ESISTE PER CHIUDERE, misurato in esercizio il 2026-09-05 su
 * `stg-tma` col monolite Laravel spento (503 su ogni rotta): la scheda Tornei mostrava
 * **«No tournaments in this category.»** mentre il backend rifiutava tutto.
 *
 * ⚠️ E' peggio sia dello spinner sia dell'errore, e va detto perche' non venga «semplificato»
 * di nuovo: uno spinner **tace**, un errore **dice che non sa**, un vuoto **afferma un fatto** —
 * e quel fatto era falso. Sulla stessa schermata, un'ora prima, il bundle precedente diceva
 * onestamente «Failed to load tournaments».
 *
 * La causa non e' un ramo sbagliato ma una PREMESSA sbagliata: il vuoto veniva dedotto da
 * `!isLoading && !isError && rows.length === 0`, cioe' da tre stati del CLIENT. Ma
 * `isLoading` in RTK Query e' vero solo al PRIMO caricamento (dopo, un giro di
 * `pollingInterval` e' `isFetching`), e `isError` e' lo stato CORRENTE. Fra un tentativo
 * fallito e il successivo esiste quindi una finestra in cui nessuno dei tre e' vero e la
 * lista e' vuota — e li' l'app affermava.
 *
 * ⇒ La regola giusta non guarda gli stati del client, guarda il SERVER:
 *
 *   📌 **Un vuoto si dichiara solo quando il server ha risposto, e la risposta era vuota.**
 *
 * `data !== undefined` e' esattamente questo: RTK Query lo popola solo con una risposta
 * riuscita, e non lo cancella su un errore successivo. Non c'e' finestra in cui sia vero
 * senza che il server abbia parlato almeno una volta.
 */

/** Lo stato di una gamba della lista, ridotto a cio' che serve per decidere. */
export interface ListLeg {
  /** `true` se quella gamba e' attiva in questa vista; se non lo e', non ha voce. */
  readonly active: boolean;
  /** `true` se il server ha risposto ALMENO UNA VOLTA con successo. */
  readonly answered: boolean;
}

/**
 * `true` solo se **ogni gamba attiva** ha ricevuto una risposta.
 *
 * ⚠️ Una gamba NON attiva conta come «ha risposto», e non e' una scorciatoia: una vista che
 * non interroga quella fonte non puo' restare in attesa di lei per sempre. La vista
 * `community` non ha gamba Laravel: senza questa regola non mostrerebbe mai il proprio
 * messaggio di vuoto, che li' e' l'informazione giusta.
 *
 * ⚠️ E con ZERO gambe attive il risultato e' `true`: nessuna fonte da interrogare significa
 * che il vuoto e' la verita' completa disponibile, non un'ignoranza.
 */
export function everyLegAnswered(legs: readonly ListLeg[]): boolean {
  return legs.every((leg) => !leg.active || leg.answered);
}

/**
 * Se l'assenza di righe va mostrata come VUOTO (`true`) o come impossibilita' di caricare
 * (`false`).
 *
 * @param rowCount quante righe si stanno per mostrare
 * @param legs     le gambe della lista
 */
export function canClaimEmpty(rowCount: number, legs: readonly ListLeg[]): boolean {
  // Con delle righe la domanda non si pone: non e' vuoto.
  if (rowCount > 0) return false;
  return everyLegAnswered(legs);
}

/* ────────────────────────────────────────────────────────────────────────────
 * Il guasto DOPO quello sopra, misurato il 2026-09-06 sulla build `stg-tma` con
 * tutti i tab della Hub accesi.
 *
 * 🔴 `canClaimEmpty` chiude il caso «il server non ha risposto». Non chiude quello
 * che si e' visto qui: **il server non e' stato nemmeno interrogato**, perche' la
 * fonte e' spenta da un flag di build (`VITE_FEATURE_QDUELS_TOURNAMENTS_ENABLED=false`
 * su questo tenant, dove qduels non ha nemmeno una resource Logto). Con la query in
 * `skip`, RTK Query non carica, non fallisce e non risponde: **non succede niente**.
 * E dove non succede niente, `canClaimEmpty(0, [])` risponde `true` — per scelta
 * esplicita, documentata sopra — e lo schermo afferma «No tournaments in this
 * category» / «Nothing to watch right now».
 *
 * ⚠️ La regola scritta sopra non e' sbagliata: e' AMBIGUA, e l'ambiguita' sta tutta
 * nel booleano `active`. Oggi `active: false` dice insieme due cose opposte:
 *   · «questa vista non interroga quella fonte» — la vista `community` non ha gamba
 *     Laravel, e tacere e' giusto: quella fonte non ha voce in capitolo;
 *   · «questa build non PUO' interrogarla» — qduels spento: e questo va DETTO, perche'
 *     l'elenco che l'utente sta guardando non e' l'elenco, e' cio' che ne resta.
 * Un solo booleano per due fatti opposti e' la stessa forma del guasto di sopra, un
 * livello piu' in alto.
 *
 * ⇒ `listVerdict` separa le due cose e non usa piu' `active`:
 *   · una fonte NON pertinente **non si passa affatto** (assenza = nessuna voce);
 *   · una fonte pertinente ma spenta si passa con `reachable: false`, e produce
 *     `unavailable` — che e' una frase onesta sullo schermo, non un silenzio.
 *
 * 📌 **Un vuoto e' un'affermazione sull'INSIEME.** Se una fonte pertinente non e' stata
 * interrogata, l'insieme non lo conosco: `unavailable` vince su `empty` anche quando
 * l'altra gamba ha risposto vuota. Perde invece contro `rows` (qualcosa da mostrare
 * c'e'), contro `loading` (non anticipo un verdetto che sta per arrivare) e contro
 * `error` (una fonte che ha FALLITO e' una notizia peggiore, e piu' azionabile, di una
 * che non c'e').
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Cosa mostrare al posto di un elenco.
 *
 * Due stati nuovi rispetto a `canClaimEmpty`, e sono nuovi per ragioni diverse:
 *  · `unavailable` — la fonte e' spenta da configurazione: la domanda non e' stata posta e
 *    non lo sara'. Questa build poteva gia' produrlo e non sapeva dirlo.
 *  · `slow` — 🔴 **e' uno STATO DI TRANSIZIONE, non un verdetto**: stiamo ancora aspettando,
 *    ma da piu' di quanto sia onesto tacere. Aggiunto il 2026-09-06 dopo una rettifica di
 *    or45: gli «spinner infiniti» misurati su `stg-tma` **non erano infiniti**, si fermavano
 *    da soli dopo 15-25 s dicendo l'errore vero. Una scadenza che a 12 s avesse dichiarato
 *    `error` avrebbe **affermato un fallimento non ancora avvenuto** — la stessa colpa del
 *    vuoto affermato, commessa dalla difesa contro di esso.
 *    ⛔ `slow` cede il posto a `error` e a `rows` appena esistono: e' sotto entrambi
 *    nell'ordine, e non e' un'affermazione su come andra' a finire.
 */
export type ListVerdict = 'rows' | 'loading' | 'slow' | 'error' | 'unavailable' | 'empty';

/**
 * Una fonte **pertinente** alla vista. Le fonti non pertinenti non si passano.
 */
export interface ListSource {
  /**
   * `false` = spenta da configurazione di build: non e' stata interrogata e non lo
   * sara'. ⛔ Non collassarlo su `loading`/`failed`: quelli descrivono una chiamata
   * partita, questo descrive una chiamata che non parte.
   */
  readonly reachable: boolean;
  /** Primo caricamento in corso (RTK `isLoading`). */
  readonly loading: boolean;
  /**
   * Stiamo aspettando da piu' del ragionevole (`hooks/useStalled.ts`). Non e' un fallimento
   * e non lo anticipa: dice solo che il silenzio e' durato troppo per restare tale.
   */
  readonly slow?: boolean;
  /** Ultimo tentativo fallito (RTK `isError`). */
  readonly failed: boolean;
  /** Il server ha risposto con successo almeno una volta (RTK `data !== undefined`). */
  readonly answered: boolean;
}

/**
 * Il verdetto per una lista, date le sue fonti pertinenti.
 *
 * @param rowCount quante righe si stanno per mostrare
 * @param sources  le fonti PERTINENTI alla vista (le altre si omettono)
 */
export function listVerdict(rowCount: number, sources: readonly ListSource[]): ListVerdict {
  if (rowCount > 0) return 'rows';
  const live = sources.filter((s) => s.reachable);
  // ⛔ `error` PRIMA di `slow`, e l'ordine e' il punto: un fallimento vero dice la causa,
  // «ci sta mettendo troppo» no. La lentezza non deve mai coprire una notizia migliore.
  // Fallita, oppure muta senza star caricando: in entrambi i casi non so, e lo dico.
  if (live.some((s) => s.failed || (!s.answered && !s.loading))) return 'error';
  // Ancora in volo, ma da troppo: si dice che ci sta mettendo troppo, non che e' andata male.
  if (live.some((s) => s.loading && s.slow)) return 'slow';
  if (live.some((s) => s.loading)) return 'loading';
  // Qui ogni fonte viva ha risposto vuoto. Se qualcuna e' spenta, il vuoto non e' mio
  // da affermare.
  if (live.length < sources.length) return 'unavailable';
  return 'empty';
}
